import { Box, IconButton, Typography, styled, TextField, Pagination, Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, Select } from '@mui/material';
import { buildingRelatedForms, EFieldType, EValueType, IField, IForm, IFormBuilding, IFormPropertyGroup, IPage, propertyGroupRelatedForms } from '../../models/form/form';
import { ChangeEvent, useEffect, useState } from 'react';
import MuiRestoreIcon from '@mui/icons-material/Restore';
import MuiSaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Theme } from '@mui/system';
import FormService from '../../services/form/FormService';
import EditFieldModal from '../../components/form/modal/EditFieldModal';
import FormField from '../../components/form/FormField';
import EditOnly from '../../components/permissions/EditOnly';
import { canEdit } from '../../utils/permissions';
import { ELanguage } from '../../models/enums/language';
import ShortTextIcon from '@mui/icons-material/ShortText';
import NotesIcon from '@mui/icons-material/Notes';
import PinIcon from '@mui/icons-material/Pin';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ListIcon from '@mui/icons-material/List';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import StarIcon from '@mui/icons-material/Star';
import { theme } from '../../styles/themes/mainTheme';
import MultiFreeInput from '../../components/input/form/MultiFreeInput';
import { validEmailRegex } from '../../utils/regex';
import { useLocation, useNavigate, useBlocker } from 'react-router-dom';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveFormChangesModal from '../../components/form/modal/SaveFormChangesModal';
import auth from '../../services/auth/AuthService';
import FormBuildingElement from '../../components/form/FormBuildingElement';
import IAdminPageBuildingDetail from '../../models/adminPage/adminPageBuildingDetail';
import BuildingService from '../../services/building/BuildingService';
import { IPropertyGroup } from '../../models/propertyGroup';
import propertyGroupService from '../../services/propertyGroupService/PropertyGroupService';
import FormPropertyGroupElement from '../../components/form/FormPropertyGroupElement';

const EditFormWrapper = styled(Box)(() => ({
    width: '100%',
}));

const FormPageWrapper = styled(Paper)(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: '2%',
}));

const ActionBarWrapper = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 4,
    paddingInline: '5%'
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    textAlign: 'center',
    marginBottom: 4
}));

const defaultField: IField = {
    id: '',
    fieldType: EFieldType.Text,
    valueType: EValueType.SingleLineText,
    multiselect: false,
    position: 0,
    options: [],
    required: false,
    text: '',
    values: [],
    isTranslated: false
};

type IconProps = {
    isDisabled?: boolean;
};

const getIconColor = (theme: Theme, isDisabled: boolean | undefined) => (isDisabled ? theme.palette.text.disabled : theme.palette.text.primary);

const SaveIcon = styled(MuiSaveIcon)<IconProps>(({ theme, isDisabled: isFormModified }) => ({
    color: getIconColor(theme, isFormModified)
}));

const RestoreIcon = styled(MuiRestoreIcon)<IconProps>(({ theme, isDisabled: isFormModified }) => ({
    color: getIconColor(theme, isFormModified)
}));

const FormTemplateEditPage = () => {
    const { t } = useTranslation(['form', "common"]);
    const navigate = useNavigate();

    const { form } = useLocation().state as { form: IForm };
    const [currentForm, setCurrentForm] = useState<IForm>();
    const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);
    const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);

    const [isFormModified, setIsFormModified] = useState<boolean>(false);
    const [formBackup, setFormBackup] = useState<IForm>();

    const [isShowEditField, setIsShowEditField] = useState<boolean>(false);
    const [fieldToEdit, setFieldToEdit] = useState<IField>();
    const [errorMessage, setErrorMessage] = useState('');

    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
    const [currentPage, setCurrentPage] = useState<IPage>();
    const [isDeletePageDialogOpen, setIsDeletePageDialogOpen] = useState<boolean>(false);

    // Blocks page navigation if form is not saved
    let blocker = useBlocker(() => isFormModified && !!(auth.user) && canEdit());

    useEffect(() => {
        // Blocks page reload if form is not saved
        const handleBeforeUnload = (event: BeforeUnloadEvent) => isFormModified && event.preventDefault();

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isFormModified]);

    useEffect(() => {
        if (!currentForm) return;

        setCurrentPage(currentForm.pages.find((page) => page.pageNumber === currentPageNumber));
    }, [currentPageNumber, currentForm]);

    useEffect(() => {
        BuildingService.GetBuildingsDetails().then(setBuildings);
        propertyGroupService.GetPropertyGroups().then(setPropertyGroups);
    }, []);

    useEffect(() => {
        setCurrentForm(structuredClone(form));
        setFormBackup(structuredClone(form));
    }, [form]);

    const onInsertField = (newField: IField) => {
        if (!currentForm) return;

        setIsFormModified(true);

        let fields = currentPage?.fields || [];
        let newPos = newField.position;

        fields = fields.slice(0, newPos).concat(fields.slice(newPos).map((field) => ({ ...field, position: field.position + 1 })));
        fields.push(newField);

        setCurrentForm((old) => old ? { ...old, pages: old.pages.map((page) => (page.pageNumber === currentPageNumber ? { ...page, fields } : page)) } : old);
    };

    const onAddField = (position: number, fieldType: EFieldType, valueType: EValueType) => {
        const field: IField = {
            id: '',
            text: fieldType === EFieldType.Image ? t("form:field.uploadImages") : t("form:field.enterText"),
            fieldType: fieldType,
            valueType: valueType,
            required: false,
            multiselect: false,
            options: [
                { id: 0, value: 'Option 1' },
                { id: 1, value: 'Option 2' },
                { id: 2, value: 'Option 3' }
            ],
            position: position,
            values: [],
            isTranslated: false
        }
        onInsertField(field)
        onEditField(field)
    };

    const onDragEnd = (draggable: DropResult) => {
        if (!draggable.destination || !currentForm) return;
        setIsFormModified(true);
        const source = draggable.source.index;
        const destination = draggable.destination.index;
        let changedFields = currentPage?.fields || [];
        const element = changedFields.splice(source, 1);
        const items = [...changedFields.slice(0, destination), ...element, ...changedFields.slice(destination, changedFields.length)];

        items.forEach((field, i) => (field.position = i));
        setCurrentForm((old) => old && { ...old, pages: old.pages.map((page) => (page.pageNumber === currentPageNumber ? { ...page, fields: items } : page)) });
    };

    const onDeleteField = (position: number) => {
        if (!currentForm) return;

        const fields = currentPage?.fields || [];
        const replacedField = fields.find((field) => field.position === position);
        if (!replacedField || !currentForm) return;

        setIsFormModified(true);

        const changedFields = fields
            .filter((field) => field.position !== position)
            .map((field) => {
                if (field.position < position) return field;
                return { ...field, position: field.position - 1 };
            });

        setCurrentForm((old) => old && { ...old, pages: old.pages.map((page) => (page.pageNumber === currentPageNumber ? { ...page, fields: changedFields } : page)) });
    };

    const onEditField = (field: IField) => {
        setIsShowEditField(true);
        setIsFormModified(true);
        setFieldToEdit(field);
    };

    const editField = (field: IField) => {
        setCurrentForm((old) => {
            if (!old) return old;

            const updatedPages = old.pages.map((page) => {
                if (page.pageNumber !== currentPageNumber) return page;

                const updatedFields = page.fields.filter((oldField) => oldField.position !== field.position).concat([field]);
                return { ...page, fields: updatedFields };
            });

            return { ...old, pages: updatedPages };
        });
    };

    const restoreForm = () => {
        if (!formBackup) return;

        setCurrentForm(structuredClone(formBackup));
        setIsFormModified(false);
    };

    const saveForm = async () => {
        if (!currentForm) return;

        try {
            await FormService.PutFormTemplate(currentForm)
            setIsFormModified(false);
            navigate(-1)
        } catch (error) {
            setErrorMessage('form:error.formSentError');
        }
    };

    const onSetFormBuildingActive = (formBuilding: IFormBuilding, isActive: boolean) => {
        if (!currentForm) return;
        const formBuildingIndex = currentForm.buildings.findIndex((b) => b.buildingId == formBuilding.buildingId)
        if (formBuildingIndex < 0) return;

        const newFormBuildings = [...currentForm.buildings]
        newFormBuildings[formBuildingIndex].isActive = isActive

        setCurrentForm((old) => old && { ...old, buildings: newFormBuildings });
    }

    const onSetFormPropertyGroupActive = (formPropertyGroup: IFormPropertyGroup, isActive: boolean) => {
        if (!currentForm) return;
        const formPropertyGroupIndex = currentForm.propertyGroups.findIndex((pg) => pg.propertyGroupId == formPropertyGroup.propertyGroupId)
        if (formPropertyGroupIndex < 0) return;

        const newFormPropertyGroups = [...currentForm.propertyGroups]
        newFormPropertyGroups[formPropertyGroupIndex].isActive = isActive

        setCurrentForm((old) => old && { ...old, propertyGroups: newFormPropertyGroups });
    }

    const updateEmails = (emails: string[]) => {
        if (currentForm && ((currentForm.emails && currentForm.emails.length < emails.length) || (!currentForm.emails && emails.length))) {
            const newEmail = emails[emails.length - 1];

            if (!validEmailRegex.test(newEmail)) {
                setErrorMessage("form:error.emailInvalid");
                return false;
            }
        }

        setCurrentForm((old) => old && { ...old, emails });
        return true;
    }

    const onAddPage = () => {
        if (!currentForm) return;

        const newPage: IPage = {
            pageNumber: currentForm.pages.length + 1,
            fields: []
        };

        setCurrentForm((old) => old && { ...old, pages: [...old.pages, newPage] });
        setCurrentPageNumber(newPage.pageNumber);
    };

    const onDeletePage = () => {
        if (!currentForm) return;

        const newPages = currentForm.pages.filter((page) => page.pageNumber !== currentPageNumber);
        newPages.forEach((page, i) => (page.pageNumber = i + 1));

        setCurrentForm((old) => old && { ...old, pages: newPages });
        setCurrentPageNumber(newPages[newPages.length - 1].pageNumber);
        setIsFormModified(true);
        setIsDeletePageDialogOpen(false);
    };

    const onAutomaticSaveForm = async () => {
        if (!currentForm) return;

        try {
            await FormService.PutFormTemplate(currentForm)
            if (blocker.proceed) blocker.proceed();
        } catch (error) {
            setErrorMessage('form:error.formSentError');
        }
    }

    const onExitForm = () => {
        if (blocker.proceed) blocker.proceed();
    }

    const onCloseChangesModal = () => {
        if (blocker.reset) blocker.reset();
    }

    return (
        <Box>
            <SaveFormChangesModal isModalVisible={blocker.state === "blocked"} onSave={onAutomaticSaveForm} onExit={onExitForm} onClose={onCloseChangesModal} />

            <FormPageWrapper>
                {isShowEditField && <EditFieldModal isShowEdit={isShowEditField} setIsShowEdit={setIsShowEditField} fieldToEdit={fieldToEdit ?? defaultField} editField={editField} />}

                {currentForm && (
                    <EditFormWrapper>
                        <ActionBarWrapper>
                            <Typography>{t('form:formName')}</Typography>
                            <TextField
                                sx={{ width: '80%' }}
                                required={true}
                                value={currentForm.name}
                                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                    setIsFormModified(true);
                                    setCurrentForm((old) => old && { ...old, name: e.target.value });
                                }}
                            />
                        </ActionBarWrapper>
                        <ActionBarWrapper>
                            <Typography>{t('form:description')}</Typography>
                            <TextField
                                sx={{ width: '80%' }}
                                required={true}
                                value={currentForm.description}
                                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                    setIsFormModified(true);
                                    setCurrentForm((old) => old && { ...old, description: e.target.value });
                                }}
                            />
                        </ActionBarWrapper>
                        <ActionBarWrapper>
                            <Typography>{t('form:language')}</Typography>
                            <TextField
                                sx={{ width: '80%' }}
                                value={t(`common:header.${ELanguage[currentForm.lang].toLowerCase()}`)}
                                disabled
                            />
                        </ActionBarWrapper>
                        {buildingRelatedForms.includes(currentForm.formType) && (
                            <>
                                <ActionBarWrapper>
                                    <Typography>{t('form:buildings')}</Typography>
                                    <Select
                                        sx={{ width: '80%', height: '2em' }}
                                        multiple
                                        value={currentForm.buildings.map((b) => b.buildingId)}
                                        onChange={(e) => {
                                            setIsFormModified(true);
                                            setCurrentForm((old) => old && {
                                                ...old, buildings: (e.target.value as number[]).map((buildingId) => ({
                                                    id: 0,
                                                    formId: old.id,
                                                    buildingId: buildingId,
                                                    isActive: old.buildings.find((b) => b.buildingId == buildingId)?.isActive || false
                                                }))
                                            });
                                        }}
                                        MenuProps={{ style: { overflowY: 'visible' } }}
                                    >
                                        {buildings.map((building) => (
                                            <MenuItem
                                                key={`building_${building.id}`}
                                                value={building.id}
                                            >
                                                {building.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </ActionBarWrapper>

                                <ActionBarWrapper sx={{ alignItems: 'flex-start' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {currentForm.buildings.length > 1 &&
                                            <>
                                                <Button variant="contained" color="primary" size="small" sx={{ marginBottom: '2%' }}
                                                    onClick={() => {
                                                        currentForm.buildings.forEach((building) => onSetFormBuildingActive(building, true));
                                                        setIsFormModified(true);
                                                    }}>
                                                    {t('form:button.activateAll')}
                                                </Button>

                                                <Button variant="contained" color="inherit" size="small"
                                                    onClick={() => {
                                                        currentForm.buildings.forEach((building) => onSetFormBuildingActive(building, false));
                                                        setIsFormModified(true);
                                                    }}>
                                                    {t('form:button.disableAll')}
                                                </Button>
                                            </>
                                        }
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '80%'
                                    }}>
                                        {currentForm.buildings.map((building) => (
                                            <FormBuildingElement
                                                formBuilding={building}
                                                onSetFormBuildingActive={(buildingForm) => {
                                                    onSetFormBuildingActive(buildingForm, !building.isActive);
                                                    setIsFormModified(true);
                                                }}
                                                buildingName={buildings.find((b) => b.id == building.buildingId)?.name || ""}
                                            />
                                        ))}
                                    </Box>
                                </ActionBarWrapper>
                            </>
                        )}

                        {propertyGroupRelatedForms.includes(currentForm.formType) && (
                            <>
                                <ActionBarWrapper>
                                    <Typography>{t('form:propertyGroups')}</Typography>
                                    <Select
                                        sx={{ width: '80%', height: '2em' }}
                                        multiple
                                        value={currentForm.propertyGroups.map((pg) => pg.propertyGroupId)}
                                        onChange={(e) => {
                                            setIsFormModified(true);
                                            setCurrentForm((old) => old && {
                                                ...old, propertyGroups: (e.target.value as number[]).map((propertyGroupId) => ({
                                                    id: 0,
                                                    formId: old.id,
                                                    propertyGroupId: propertyGroupId,
                                                    isActive: old.propertyGroups.find((pg) => pg.propertyGroupId == propertyGroupId)?.isActive || false
                                                }))
                                            });
                                        }}
                                        MenuProps={{ style: { overflowY: 'visible' } }}
                                    >
                                        {propertyGroups.map((propertyGroup) => (
                                            <MenuItem
                                                key={`propertyGroup_${propertyGroup.id}`}
                                                value={propertyGroup.id}
                                            >
                                                {propertyGroup.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </ActionBarWrapper>

                                <ActionBarWrapper sx={{ alignItems: 'flex-start' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {currentForm.propertyGroups.length > 1 &&
                                            <>
                                                <Button variant="contained" color="primary" size="small" sx={{ marginBottom: '2%' }}
                                                    onClick={() => {
                                                        currentForm.propertyGroups.forEach((propertyGroup) => onSetFormPropertyGroupActive(propertyGroup, true));
                                                        setIsFormModified(true);
                                                    }}>
                                                    {t('form:button.activateAll')}
                                                </Button>

                                                <Button variant="contained" color="inherit" size="small"
                                                    onClick={() => {
                                                        currentForm.propertyGroups.forEach((propertyGroup) => onSetFormPropertyGroupActive(propertyGroup, false));
                                                        setIsFormModified(true);
                                                    }}>
                                                    {t('form:button.disableAll')}
                                                </Button>
                                            </>
                                        }
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '80%'
                                    }}>
                                        {currentForm.propertyGroups.map((propertyGroup) => (
                                            <FormPropertyGroupElement
                                                formPropertyGroup={propertyGroup}
                                                onSetFormPropertyGroupActive={(propertyGroupForm) => {
                                                    onSetFormPropertyGroupActive(propertyGroupForm, !propertyGroup.isActive);
                                                    setIsFormModified(true);
                                                }}
                                                propertyGroupName={propertyGroups.find((pg) => pg.id == propertyGroup.propertyGroupId)?.name || ""}
                                            />
                                        ))}
                                    </Box>
                                </ActionBarWrapper>
                            </>
                        )}

                        <ActionBarWrapper>
                            <Typography>{t('form:emails')}</Typography>
                            <MultiFreeInput
                                width={'80%'}
                                valueArray={currentForm.emails || []}
                                setValueArray={(valueArray) => {
                                    setErrorMessage("")
                                    setIsFormModified(true);
                                    return updateEmails(valueArray);
                                }}
                                setErrorMessage={(errorMessage) => setErrorMessage(errorMessage)}
                            />
                        </ActionBarWrapper>
                        {errorMessage && <ErrorMessage>*{t(errorMessage)}</ErrorMessage>}
                        <ActionBarWrapper marginTop={2}>
                            <Box>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Text, EValueType.SingleLineText)}>
                                    <TextFieldsIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Input, EValueType.SingleLineText)}>
                                    <ShortTextIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Input, EValueType.MultiLineText)}>
                                    <NotesIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Input, EValueType.Number)}>
                                    <PinIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Input, EValueType.Date)}>
                                    <CalendarMonthIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Input, EValueType.Boolean)}>
                                    <CheckBoxIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Select, EValueType.SingleLineText)}>
                                    <ListIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Image, EValueType.SingleLineText)}>
                                    <PermMediaIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                                <IconButton onClick={() => onAddField(currentPage?.fields.length ?? 0, EFieldType.Rating, EValueType.SingleLineText)}>
                                    <StarIcon sx={{ color: theme.palette.text.primary }} />
                                </IconButton>
                            </Box>
                            <EditOnly>
                                <Box>
                                    <IconButton onClick={restoreForm} disabled={!isFormModified}>
                                        <RestoreIcon isDisabled={!isFormModified} />
                                    </IconButton>
                                    <IconButton onClick={saveForm} disabled={!isFormModified}>
                                        <SaveIcon isDisabled={!isFormModified} />
                                    </IconButton>
                                </Box>
                            </EditOnly>
                        </ActionBarWrapper>

                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="forms">
                                {(provided) => (
                                    <ul style={{ listStyle: "none" }} {...provided.droppableProps} ref={provided.innerRef}>
                                        {currentPage?.fields
                                            .sort((a, b) => a.position - b.position)
                                            .map((field, index) => (
                                                <Draggable key={String(index)} draggableId={String(index)} index={index}>
                                                    {(provided) => (
                                                        <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                            <FormField key={field.position} deleteField={onDeleteField} editField={onEditField} field={field} />
                                                        </li>
                                                    )}
                                                </Draggable>
                                            ))}
                                        {provided.placeholder}
                                    </ul>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <Box sx={{ display: "flex", justifyContent: "space-evenly", mt: 8 }}>
                            <EditOnly>
                                <Button onClick={() => setIsDeletePageDialogOpen(true)} startIcon={<DeleteIcon />} disabled={currentForm.pages.length <= 1} variant="contained" color="error">{t('form:deletePage')}</Button>
                            </EditOnly>

                            <Pagination
                                count={currentForm.pages.length}
                                color="primary"
                                onChange={(e, value) => setCurrentPageNumber(value)}
                                page={currentPageNumber}
                            />

                            <EditOnly>
                                <Button onClick={onAddPage} startIcon={<NoteAddIcon />} variant="contained" color="primary">{t('form:addPage')}</Button>
                            </EditOnly>

                            <Dialog
                                open={isDeletePageDialogOpen}
                                onClose={() => setIsDeletePageDialogOpen(false)}
                            >
                                <DialogTitle>{t('form:deletePage')}</DialogTitle>
                                <DialogContent>
                                    <DialogContentText >{t('form:deletePageWarning')}</DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setIsDeletePageDialogOpen(false)} sx={{ color: theme.palette.text.secondary }}>
                                        {t('common:header.cancel')}
                                    </Button>
                                    <Button onClick={onDeletePage} autoFocus color='error'>{t('common:header.continue')}</Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    </EditFormWrapper>
                )}
            </FormPageWrapper>
        </Box>
    );
};

export default FormTemplateEditPage;
